<template>
  <div
    ref="containerRef"
    class="relative h-full w-full"
  >
    <!-- Only render the actual thumbnail when visible or has been visible -->
    <PDFPageThumbnail
      v-if="shouldLoad"
      :thumbnails="thumbnails"
      :page-number="pageNumber"
      :preset="preset"
      class="h-full w-full"
    />

    <!-- Placeholder while not yet loaded -->
    <div
      v-else
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div class="flex flex-col items-center gap-2">
        <FileText class="h-8 w-8 text-gray-300" />
        <span class="text-center text-xs text-gray-400">Page {{ pageNumber }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  /**
   * LazyPDFPageThumbnail Component
   *
   * A viewport-aware lazy loading wrapper around PDFPageThumbnail.
   * Only loads the actual thumbnail when the component enters the viewport
   * (or comes within the specified margin).
   *
   * Features:
   * - Intersection Observer based viewport detection
   * - Configurable load margin (loads before actually visible)
   * - Graceful placeholder while not loaded
   * - Once loaded, stays loaded (no unloading)
   * - Minimal performance overhead
   *
   * Performance Benefits:
   * - Dramatically reduces initial DOM complexity
   * - Only loads thumbnails user can see + buffer
   * - Perfect for large documents (1000+ pages)
   * - Smooth scrolling performance
   *
   * @example
   * ```vue
   * <LazyPDFPageThumbnail
   *   :thumbnails="documentThumbnails"
   *   :page-number="5"
   *   preset="thumbnail"
   *   :load-margin="300px"
   * />
   * ```
   */

  import { computed, onMounted, inject, ref } from 'vue'
  import { FileText } from 'lucide-vue-next'
  import { useViewportLoadingContinuous } from '@/composables/useViewportLoading'
  import PDFPageThumbnail from './PDFPageThumbnail.vue'
  import type { ImageproxyImagePreset } from '@/types/generated'
  import type { Ref } from 'vue'
  interface Props {
    thumbnails: ImageproxyImagePreset[] // Thumbnails array (required)
    pageNumber: number
    preset?: 'thumbnail' | 'small' | 'medium' | 'large'
    /**
     * How far before entering viewport should we start loading
     * Default: '200px' (loads 200px before visible)
     */
    loadMargin?: string
    /**
     * Force load regardless of viewport (useful for debugging)
     */
    forceLoad?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    preset: 'thumbnail',
    loadMargin: '100px',
    forceLoad: false
  })

  // Use continuous viewport loading to track current visibility
  const { isVisible, targetRef } = useViewportLoadingContinuous(props.loadMargin)

  // Inject scroll debounce state from parent (defaults to always stopped)
  const hasStoppedScrolling = inject<Ref<boolean>>('hasStoppedScrolling', ref(true))

  // Should we load the thumbnail?
  // Only load if:
  // 1. It's CURRENTLY visible in viewport AND
  // 2. User has stopped scrolling (or forceLoad is true)
  const shouldLoad = computed(() => {
    return props.forceLoad || (isVisible.value && hasStoppedScrolling.value)
  })

  // Ref to the container element for intersection observer
  const containerRef = computed({
    get: () => targetRef.value,
    set: (value) => {
      targetRef.value = value
    }
  })

  onMounted(() => {
    // If forceLoad is true, we still set up the ref for consistency
    // but the thumbnail will load immediately due to shouldLoad computed
  })

  defineExpose({
    shouldLoad,
    isVisible
  })
</script>
